define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/_base/underscore",
	"csi/def",
	"csi/def/ActionFactory",
	"csi/def/Card",
	"csi/def/PreferencePath",
	"csi/def/SearchChild",
	"csi/def/View",
	"csi/def/ViewFactory",
	"csi/locale",
	"csi/reflection",
	"csi/state/viewUtils",
	"csi/util",
	"csi/xml"
], function(array, declare, lang, csi, _, def, ActionFactory, Card, PreferencePath, SearchChild, View, ViewFactory, locale, reflection, viewUtils, util, xml) {

	var VIEW = "View";

	function _addCards(/*Object*/map, /*XML/JSNode[]*/cards) {
		array.forEach(cards, function(c) {
			var id = c.m_id;
			if (id && !map[id]) {
				map[id] = c;
			}
		});
	}

	function _addHeadings(/*Object*/map, /*XML/JSNode[]*/headings) {
		array.forEach(headings, function(h) {
			var key = def.getHeadingKey(h.getAttribute("Path"), h.getAttribute("Id"));
			if (key && !map[key]) {
				map[key] = h;
			}
		});
	}

	/**
	 * Parse image definition for node type
	 * @param {DefElement} xn 
	 * @return {ITypeImage | undefined}
	 */
	function _parseImage(xn) {
		if (xn) {
			const m_id = util.getProperty(xn, "Id");
			const m_path = util.getProperty(xn, "Path");
			const m_index = util.getProperty(xn, "Index");

			return {
				m_id,
				m_path,
				m_index,
			};
		}
	}

	var _Type = declare(null, {
		// String: class to which members apply
		m_idType: null,

		// Node: type definition overrides from View XML NodeRef tag (NodeRef Override View by Id)
		m_defRefs: null,

		// Action[]: array of actions to add to breadcrumb trail
		m_actions: null,

		// Boolean: true if type has layout view or is master class
		bNavigate: false,

		// {String:Card}: cards mapped by id (NodeRef Override by Card Id)
		cards: null,

		// {String:XMLNode}: the array of XML nodes representing Heading-s under this Node mapped by Id
		headingXmlNodes: null,

		/** @property {Object} image - the object holds the special path of the image for this type */
		image: null,

		// PreferencePath[]: default preference paths for class (NodeRef Merge)
		m_prefPaths: null,

		// SearchChild[]: array of child search definitions (NodeRef Override)
		searches: null,

		//String: state heading to place on the breadcrumb
		stateHeading: null,

		// csi.tabs.View: root tabs view for type
		layoutView: null,

		// XMLNode: type definition from View XML Node tag
		_xn: null,


		constructor: function(/*XMLNode*/xn, /*Type[]?*/typeRefs, /*String*/type) {
			// summary: merge Node and NodeRef tags - typeRefs come in ordered from last to first in terms of module load order
			this.m_idType = type;

			if (!xn) {
				return;
			}

			this.cards = util.getDefs(xn, "Card", Card);
			this.image = util.getDef(xn, "Image", _parseImage, true);
			this.m_prefPaths = util.getDefs(xn, "PreferencePath", PreferencePath) || [];
			this.headingXmlNodes = util.sn(xn, "Headings/Heading");	// it should be an array of pure XMLNode/JSNode
			this.searches = util.getDefs(xn, "Search/Child", SearchChild);
			this._xn = xn;

			if (xn.tagName == "NodeRef") {
				return;
			}

			// parameters only available on Node tag
			var mapCards = {};
			var mapHeadings = {};

			this.stateHeading = util.getProperty(xn, "StateHeading");

			this.m_actions = util.getDefs(xn, "Actions/child::*", ActionFactory.newAction, true);

			if (typeRefs?.length) {
				// merge cards, paths and headings - order is last to first
				let aPaths = [];

				typeRefs.concat([this]).forEach((tr) => {
					aPaths = aPaths.concat(tr.m_prefPaths);
					_addCards(mapCards, tr.cards);
					_addHeadings(mapHeadings, tr.headingXmlNodes);
				});

				this.m_prefPaths = aPaths;

				// m_defRefs will store reference to XML definition - order is last to first
				this.m_defRefs = [];

				// merge Module tags and search overrides - order is first to last
				array.forEach(typeRefs.reverse(), function(tr, nRef) {
					// process Module tags in order of definition - merge with most recent definition of view
					var modules = util.sn(tr._xn, "Module");

					array.forEach(modules, function(m) {
						var id = m.getAttribute("Id");
						var v = this.getViewTag(id);
						if (v) {
							array.forEach(util.sn(m, "child::*"), function(vr) {
								v.appendChild(vr);
							});
						} else {
							console.warn("Cannot find the view with Id='" + id + "' for the Module in type=" + this.m_idType);
						}
					}, this);

					// store defintion for view lookup - do so here so getViewTag call inside above loop does not find
					// view definitions defined in current or subsequently loaded NodeRef
					this.m_defRefs.unshift(tr._xn);

					// if child searches defined in another module, then they override the Node definition
					if (tr.searches) {
						this.searches = tr.searches;
					}

					// override the image tag if any
					if (tr.image) {
						this.image = tr.image;
					}
				}, this);
			} else {
				// turns array of cards and headings into map by id
				_addCards(mapCards, this.cards);
				_addHeadings(mapHeadings, this.headingXmlNodes);
			}

			this.cards = mapCards;
			this.headingXmlNodes = mapHeadings;

			// look for layout view - do not use getViewDef here so that we don't get console warning
			var idHostView = def.getHostViewId();
			var xnHost = this.getViewTag(idHostView);
			if (xnHost) {
				this.layoutView = this._createViewDef(xnHost);
			}

			const rc = reflection.getClass(this.m_idType);

			// add preference path to CurrentRevision for master class
			if (rc?.isMaster()) {
				const bCurrent = this.m_prefPaths.some((p) => p.m_path == reflection.PATH_CURRENT_REVISION);
				if (!bCurrent) {
					this.m_prefPaths.push(lang.mixin(new PreferencePath(), {
						m_path: reflection.PATH_CURRENT_REVISION
					}));
				}
			}

			// add preference path for appropriate relations
			rc?.customPreferencePaths.forEach((path) => {
				this.m_prefPaths.push(lang.mixin(new PreferencePath(), {
					m_path: path,
				}));
			});
		},

		resolve: function() {
			// summary: resolve locale for definitions
			var lr = locale.getResolver({typeContext: this.m_idType});
			var rc = reflection.getClass(this.m_idType);

			def.resolveDefs(this.m_actions, lr, rc);
			def.resolveDefs(this.searches, lr, rc);
			def.resolveDefs(this.cards, lr, rc);
		},

		getName: function() {
			return locale.type(this.m_idType);
		},

		getType: function() {
			return this.m_idType;
		},

		getCard: function(idCard) {
			return this.cards ? this.cards[idCard] : null;
		},

		getPreferencePaths: function() {
			return this.m_prefPaths;
		},

		getChildSearches: function() {
			return this.searches;
		},

		getChildSearch: function(idAttr) {
			return csi.search(this.searches, function(defSearch) {
				return (defSearch.id == idAttr) && !defSearch.path;
			});
		},

		getChildTag: function(/*String*/tag, /*String*/id) {
			// summary: return XMLNode with given tag and id for type
			if (!this._xn) {
				return null;
			}

			var xpath = tag + '[@Id="' + id + '"]';
			var xnChild;

			array.some(this._getNodeDefs(), function(xn) {
				return xnChild = util.ssn(xn, xpath);
			}, this);

			return xnChild;
		},

		getHeadingXML: function(key) {
			// summary: key is compose of id and optional path
			return this.headingXmlNodes && this.headingXmlNodes[key];
		},

		/**
		 * Return definition for given view
		 * @param {string} idView 
		 * @param {string} [typeView]
		 * @return {View}
		 */
		getViewDef: function(idView, typeView) {
			var xn = this.getViewTag(idView);
			if (xn) {
				return this._createViewDef(xn, typeView);
			}

			console.warn("Cannot find the view with Id='" + idView + " for the type='" + this.getType() + "'");
			return null;
		},

		/**
		 * Return view type along with setting on type to use for help system
		 * @param {string} id 
		 * @return {[string, boolean]}
		 */
		getViewTypeAndHelpRootType(id) {
			const xn = this.getViewTag(id);
			return [util.getProperty(xn, "xsi:type", ""), util.getBooleanProperty(xn, "UseHelpRootType", false)];
		},

		/**
		 * Return type of view with given id (i.e. Table, Gallery, etc.)
		 * @param {string} id 
		 * @return {string}
		 */
		getViewType(id) {
			const xn = this.getViewTag(id);
			return this.getViewTagType(xn);
		},

		/**
		 * Return type of view for given view tag
		 * @param {DefElement} xn 
		 * @return {string}
		 */
		getViewTagType(xn) {
			return util.getProperty(xn, "xsi:type", "");
		},

		hasViewDef: function(idView) {
			return Boolean(this.getViewTag(idView));
		},

		/**
		 * Return true if layout view for type is a react component
		 */
		isReact: function() {
			const {layoutView} = this;
			const viewType = layoutView && layoutView.m_typeView;
			return viewType === viewUtils.VIEW_TYPE_REACT;
		},

		traverseViewTags: function(callback, scope) {
			_.some(this._getNodeDefs(), function(xn) {
				var viewTags = util.sn(xn, VIEW);
				return viewTags ? _.some(viewTags, callback, scope) : false;
			});
		},

		/**
		 * Create view definition from XML
		 * @param {XMLNode} xn 
		 * @param {string} [typeView]
		 * @return {View}
		 */
		_createViewDef: function(xn, typeView) {
			return ViewFactory.create(xn, typeView, this.getType());
		},

		_getNodeDefs: function() {
			if (!this._xn) {
				return [];
			}

			return this.m_defRefs ? _.union(this.m_defRefs, [this._xn]) : [this._xn];
		},

		getViewTag: function(idView) {
			var xn = this.getChildTag(VIEW, idView);
			if (!xn) {
				this.traverseViewTags(function(viewTag) {
					var alias = viewTag.getAttribute("Alias");
					if (alias && (_.indexOf(alias.split(","), idView) >= 0)) {
						xn = viewTag;
						return true;
					}

					return false;
				});
			}

			return xn;
		}
	});

	// avoid circular dependencies
	def.typeCtr = _Type;

	return _Type;
});